// Dashboard/Contacts/index.tsx

import { useCallback, useEffect, useMemo, useState } from "react";
import Loader from "../../../components/Loader";
import ListHeader from "./ListHeader";
import AppSimpleBar from "../../../components/AppSimpleBar";
import EmptyStateResult from "../../../components/EmptyStateResult";

import { divideByKey, DivideByKeyResultTypes } from "../../../utils";
import { useContacts } from "../../../hooks/useContacts";
import InviteContactModal from "../../../components/InviteContactModal";
import { inviteContact } from "../../../api";

import {
  showSuccessNotification,
  showErrorNotification,
} from "../../../helpers/notifications";

import { useChat } from "../../../context/ChatContext";
import { useAuth } from "../../../context/AuthContext";
import { IContact } from "../../../types/contacts";
import Contact from "./Contact";

interface IContactInvite {
  phone: string | null;
  name: string | null;
  message: string | null;
}

const ContactsPage = () => {
  const { user } = useAuth();
  const { handleSelectChat } = useChat();
  const { contacts, error, loading, getContacts } = useContacts();

  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // ===============================
  // LOAD CONTACTS
  // ===============================
  useEffect(() => {
    getContacts();
  }, [getContacts]);

  // ===============================
  // DERIVED CONTACT LIST (MEMO)
  // ===============================
  const contactsData = useMemo<
    DivideByKeyResultTypes<IContact>[]
  >(() => {
    if (!contacts.length) return [];

    const term = search.toLowerCase();

    const filtered = contacts.filter((c: IContact) => {
      return (
        String(c.fullName || "").toLowerCase().includes(term) ||
        String(c.phoneNumber || "").toLowerCase().includes(term) ||
        String(c.name || "").toLowerCase().includes(term)
      );
    });

    return divideByKey<IContact>("name", filtered);
  }, [contacts, search]);

  // ===============================
  // SELECT CHAT (MEMO)
  // ===============================
  const onSelectChatMemo = useCallback(
    (id: number) => {
      handleSelectChat(id);
    },
    [handleSelectChat]
  );

  // ===============================
  // SEARCH
  // ===============================
  const onChangeSearch = (value: string) => {
    setSearch(value);
  };

  // ===============================
  // INVITE CONTACT
  // ===============================
  const onInviteContact = async (data: IContactInvite) => {
    try {
      const payload = {
        idUser: user?.id || 0,
        to: data.phone ?? "",
        type: "text",
        body: data.message ?? "",
        name: data.name ?? "",
      };

      const response = await inviteContact(payload);

      if (response.id > 0) {
        showSuccessNotification("Convite enviado com sucesso!");
      } else {
        showErrorNotification("Erro ao enviar Convite!");
      }

      setIsOpen(false);

    } catch (error) {
      console.error("[SEND TEXT DIRECT] ERROR:", error);
      showErrorNotification("Erro ao enviar Convite!");
    }
  };

  const totalC = contacts.length;

  return (
    <>
      <div className="position-relative">
        {loading && <Loader />}

        <ListHeader
          search={search}
          onChangeSearch={onChangeSearch}
          openModal={() => setIsOpen(true)}
        />

        <AppSimpleBar className="chat-message-list chat-group-list">
          <div>
            {error && (
              <p className="text-danger text-center">{error}</p>
            )}

            {totalC === 0 && !loading ? (
              <EmptyStateResult searchedText={search} />
            ) : (
              contactsData.map((letterContacts, index) => (
                <Contact
                  key={letterContacts.letter}
                  letterContacts={letterContacts}
                  index={index}
                  onSelectChat={onSelectChatMemo}
                />
              ))
            )}
          </div>
        </AppSimpleBar>
      </div>

      <InviteContactModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onInvite={onInviteContact}
      />
    </>
  );
};

export default ContactsPage;
